// src/lib/theme/shadows.ts
// ============================================================================
// THE DECK COMPASS: SHADOW SYSTEM
// Extracted from Card.tsx (canonical)
// Status: LOCKED - Do not modify without design review
// ============================================================================

export const shadows = {
  // ========================================================================
  // PAPER CARD SHADOWS
  // Default shadow for cards and elevated surfaces
  // Composed of:
  //  - Inset highlight: subtle white ridge at top
  //  - Soft drop shadow: gentle depth
  // Source: Card.tsx variant="paper"
  // ========================================================================

  /**
   * Paper Default
   * Standard elevation for cards and containers.
   * Creates a subtle "paper lifted from table" effect.
   * Inset: White ridge (1px top)
   * Drop: Soft shadow (6px blur, 14px spread)
   */
  paperDefault:
    'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 6px 14px rgba(31, 42, 46, 0.10)',

  /**
   * Paper Hover
   * Slightly deeper shadow for interactive cards on hover.
   * Increases depth perception without aggression.
   * Same inset ridge, deeper drop shadow
   * Source: Card.tsx variant="interactive" hover state
   */
  paperHover:
    'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 8px 18px rgba(31, 42, 46, 0.12)',

  /**
   * Paper Active / Pressed
   * Inset shadow when element is pressed/active.
   * Creates visual feedback of "pressing into the surface"
   * Deeper inset shadow, no drop shadow
   * Source: Card.tsx variant="interactive" active state
   */
  paperActive: 'inset 0 2px 10px rgba(0, 0, 0, 0.12)',

  // ========================================================================
  // MINIMAL SHADOWS (For subtle elevation)
  // ========================================================================

  /**
   * Subtle - Minimal or no elevation
   * Used for secondary content, lower emphasis
   * Source: Card.tsx variant="subtle"
   */
  subtle: 'none',

  /**
   * Outline - Border-only, no shadow
   * For overlay cards and transparent backgrounds
   * Source: Card.tsx variant="outline"
   */
  outline: 'none',

  // ========================================================================
  // SEMANTIC SHADOW ALIASES
  // ========================================================================

  /**
   * Elevation levels for consistent hierarchy
   */
  elevation: {
    none: 'none',
    sm: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 6px 14px rgba(31, 42, 46, 0.10)',
    md: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 8px 18px rgba(31, 42, 46, 0.12)',
    lg: 'inset 0 2px 10px rgba(0, 0, 0, 0.12)',
  },

  // ========================================================================
  // INTERACTION STATES
  // ========================================================================

  state: {
    default: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 6px 14px rgba(31, 42, 46, 0.10)',
    hover: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 8px 18px rgba(31, 42, 46, 0.12)',
    active: 'inset 0 2px 10px rgba(0, 0, 0, 0.12)',
    disabled: 'none',
  },
} as const;

export type Shadows = typeof shadows;

// ============================================================================
// TAILWIND CONFIGURATION REFERENCE
// Add these to tailwind.config.js theme.extend.boxShadow:
// ============================================================================
/*
  boxShadow: {
    'paper-default': 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 6px 14px rgba(31, 42, 46, 0.10)',
    'paper-hover': 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 8px 18px rgba(31, 42, 46, 0.12)',
    'paper-active': 'inset 0 2px 10px rgba(0, 0, 0, 0.12)',
    'elevation-sm': 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 6px 14px rgba(31, 42, 46, 0.10)',
    'elevation-md': 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 8px 18px rgba(31, 42, 46, 0.12)',
    'elevation-lg': 'inset 0 2px 10px rgba(0, 0, 0, 0.12)',
  },
*/